import { GetServerSideProps } from 'next';
import ErrorPage from 'next/error';
import Link from 'next/link';
import React from 'react';

import getMenu from 'utils/getMenu';

import HeadWithSocialMedia from 'components/HeadWithSocialMedia/HeadWithSocialMedia';
import SectionWithContainer from 'components/PageBlocks/SectionWithContainer/SectionWithContainer';
import { useApp } from 'contexts/appContext';
import { IArticle } from 'models/article';
import { IDonationCampaign } from 'models/donationCampaign';
import { IMenu } from 'models/menu';
import { ArticleClientService } from 'services/articleClientService';
import { DonationCampaignClientService } from 'services/donationCampaignClientService';
import { fetcher } from 'services/httpRequestor';

import MainLayout from '../layouts/MainLayout';

// This gets called on every request
export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const httpRequestor = fetcher(req.headers.cookie);
  const articleService = new ArticleClientService(httpRequestor);
  const donationCampaignService = new DonationCampaignClientService(
    httpRequestor,
  );
  try {
    const [articles, donationCampaigns] = await Promise.all([
      articleService.getAllArticles(),
      donationCampaignService.getAllDonationsCampaigns(),
    ]);
    // Pass data to the page via props
    return { props: { articles, donationCampaigns } };
  } catch (error) {
    return {
      props: {
        statusCode: error.status,
      },
    };
  }
};

interface ISiteMapsProps {
  host: string;
  articles: IArticle[];
  donationCampaigns: IDonationCampaign[];
  statusCode?: number;
}

const SiteMap: React.FC<ISiteMapsProps> = ({
  host,
  articles,
  donationCampaigns,
  statusCode,
}) => {
  const { menuStructure } = useApp();
  const formattedMenu = getMenu(menuStructure, donationCampaigns, articles);
  if (statusCode) {
    return <ErrorPage statusCode={statusCode} />;
  }
  return (
    <>
      <HeadWithSocialMedia
        title="Surf Life Saver Giving - Site Map"
        description="Surf Life Saver Giving Site Map"
        url={`${host}/site-map`}
        keywords="site map, surf life saver, site map"
        canonical_url={`${host}/site-map`}
      />
      <MainLayout callToActionDisabled>
        <SectionWithContainer>
          <h1>Surf Life Saving Giving - Site map</h1>
        </SectionWithContainer>
        {formattedMenu.map((menu: IMenu, index) => {
          return (
            <SectionWithContainer
              key={menu.href}
              className={index % 2 === 0 ? 'bg-light-grey' : 'bg-white'}
            >
              <h3>
                <Link
                  as={menu.as}
                  href={{
                    pathname: menu.pathname,
                    query: menu.query,
                  }}
                  passHref
                >
                  {menu.label}
                </Link>
              </h3>
              {!!menu.items?.length && (
                <ul>
                  {menu.items.map((item: IMenu) => (
                    <li key={item.href}>
                      <Link
                        as={item.as}
                        href={{
                          pathname: item.pathname,
                          query: item.query,
                        }}
                        passHref
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </SectionWithContainer>
          );
        })}
      </MainLayout>
    </>
  );
};

export default SiteMap;
